import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { OarTaskApi } from '../../apis/oar-task-api';
import type { OarTaskDataLoader } from '../oar-task-data-loader';
import type { OarTaskContent } from '../../types/oar-types';
import { OarParseStatus } from '../../types/oar-types';
import {
    mapCitedDocuments,
    mapConclusiveOpinions,
    mapExaminerConclusions,
} from '../../mappers/oar-content-mapper';
import { OarTaskContentModel } from '../../models/oar-task-content-model';

/**
 * Table 数据加载器
 *
 * 负责加载审查意见通知书的表格信息，包括：
 * - 引用文献列表
 * - 结论性意见（法条违反记录）
 * - 审查员结论
 *
 * 工作模式：
 * - 当状态为 SUCCESS 时：调用 getOfficeActionTable 获取解析结果
 * - 当状态为其他状态时：调用 parseOfficeActionTable 触发解析
 */
@injectable()
export class TableDataLoader implements OarTaskDataLoader {
    readonly name = 'Table';

    constructor(
        @inject(OarTaskApi)
        private readonly _api: OarTaskApi,
        @inject(OarTaskContentModel)
        private readonly _model: OarTaskContentModel,
    ) {}

    /**
     * Table 部分总是需要加载
     */
    shouldLoad(state: OarTaskContent): boolean {
        return true;
    }

    /**
     * 加载 Table 数据
     *
     * 根据当前解析状态决定执行哪个操作：
     * - SUCCESS: 获取解析结果（调用 getOfficeActionTable）
     * - 其他状态: 触发解析（调用 parseOfficeActionTable）
     */
    async load(taskId: string): Promise<Partial<OarTaskContent>> {
        const currentState = this._model.modelData;
        const parseStatus = currentState.oarTableParseStatus;

        // 根据状态决定调用哪个 API
        let tableInfo;
        if (parseStatus === OarParseStatus.SUCCESS) {
            // 触发解析
            tableInfo = await this._api.getOfficeActionTable({
                path: { task_id: taskId },
            });
        } else {
            // 获取已解析的结果
            tableInfo = await this._api.parseOfficeActionTable({
                path: { task_id: taskId },
            });
        }

        // 使用映射器转换 API 响应到领域模型
        return {
            oarCitedDocuments: mapCitedDocuments(tableInfo.cited_documents),
            oarConclusiveOpinions: mapConclusiveOpinions(tableInfo.conclusive_opinions),
            oarExaminerConclusions: mapExaminerConclusions(tableInfo.examiner_conclusions),
        };
    }
}
